import {
  ActionButton,
  Checkbox,
  SearchBox,
  mergeStyleSets,
} from "@fluentui/react";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { FleetCountryFilter } from "components/FleetCountryFilter";
import { isFullOrInternalAdmin } from "form/util";

import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { ResultsScore } from "../../components/ResultsScore";
import { Links } from "../../components/navigation/Links";
import { HSpace, NavigationBar } from "../../components/styled";
import { routes } from "../../config/routes";
import {
  selectShownFleets,
  setCountryFilter,
  setIncludeCancelledFleets,
  setIncludeFormerClients,
  setSearchFleetsPage,
} from "../../store/fleetsPage";
import { useSelector } from "../../store/hooks";
import { AppDispatch } from "../../store/store";
import { ExportExcelFleetsButton } from "./ExportExcelFleetsButton";

type Props = {
  refExcelExport?: ExcelExport;
  excelColumns?: any[];
  fileName?: string;
};

export const Navigation = ({ refExcelExport, excelColumns, fileName }: Props) => {
  const { t } = useTranslation();
  const userRole = useSelector((s) => s.auth.userRole);
  const search = useSelector((s) => s.fleetsPage.search);
  const countryFilter = useSelector((s) => s.fleetsPage.countryFilter);
  const includeFormerClients = useSelector(
    (s) => s.fleetsPage.includeFormerClients,
  );
  const includeCancelledFleets = useSelector(
    (s) => s.fleetsPage.includeCancelledFleets,
  );
  const taxonomy = useSelector((s) => s.taxonomy);
  const dispatch: AppDispatch = useDispatch();
  const fleets = useSelector(selectShownFleets);
  const allFleets = useSelector((s) => s.fleetsPage.entities);

  // Extract unique country codes from all fleets (unfiltered) for filter options
  const countryOptions = React.useMemo(() => {
    const allFleetsArray = Object.values(allFleets);
    const uniqueCountries = [
      ...new Set(
        allFleetsArray.map((fleet) => fleet.countryCode).filter(Boolean),
      ),
    ];
    return [
      { key: "0", text: t("VehicleStatusCode.ALL") },
      ...uniqueCountries.map((code: string) => ({
        key: code,
        text: t(taxonomy.Country.byId[code]?.code) || code,
      })),
    ];
  }, [allFleets, t, taxonomy]);

  const classes = getClassNames();
  return (
    <NavigationBar>
      <Links />
      <HSpace />

      <SearchBoxContainer>
        <StyledSearchBox
          placeholder={t("bfm.searchFleets")}
          value={search}
          onChange={(_, value) => {
            dispatch(setSearchFleetsPage(value || ""));
          }}
        />
      </SearchBoxContainer>

      {countryOptions.length > 2 && (
        <>
          <HSpace width={10} />
          <FleetCountryFilter
            value={countryFilter}
            onChange={(val) => {
              dispatch(setCountryFilter(val.key));
            }}
            options={countryOptions}
          />
        </>
      )}

      <HSpace width={10} />
      <Checkbox
        label={t("bfm.includeFormerClients.label")}
        checked={includeFormerClients}
        onChange={(_, checked) => {
          dispatch(setIncludeFormerClients(checked || false));
        }}
        styles={{
          root: {
            alignItems: "center",
          },
        }}
      />
      <HSpace width={10} />
      <Checkbox
        label={t("bfm.includeCancelledFleets.label")}
        checked={includeCancelledFleets}
        onChange={(_, checked) => {
          dispatch(setIncludeCancelledFleets(checked || false));
        }}
        styles={{
          root: {
            alignItems: "center",
          },
        }}
      />
      <HSpace width={10} />

      {refExcelExport && excelColumns && fileName && (
        <>
          <ExportExcelFleetsButton
            refExcelExport={refExcelExport}
            columns={excelColumns}
            fileName={fileName}
          />
          <HSpace width={10} />
        </>
      )}

      {isFullOrInternalAdmin(userRole) && (
        <Link
          to={routes.createFleet.getPath()}
          className={classes.addFleetLink}
        >
          <ActionButton
            className={classes.addFleetBtn}
            iconProps={{ iconName: "Add" }}
            text={t("bfm.addFleet")}
          />
        </Link>
      )}
      <span className={classes.resultScoreContainer}>
        <ResultsScore score={fleets.length} />
      </span>
    </NavigationBar>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    resultScoreContainer: {
      display: "flex",
      flex: 1,
      justifyContent: "flex-end",
    },
    addFleetBtn: {
      height: "100%",
      i: {
        marginRight: 0,
      },
    },
    addFleetLink: {
      height: "100%",
    },
  });

const SearchBoxContainer = styled.div`
  .ms-SearchBox {
    background-color: ${(p) => p.theme.palette.white};
    border: 1px solid ${(p) => p.theme.palette.neutralTertiaryAlt};
    border-radius: 2px;
  }
  width: 300px;
`;

const StyledSearchBox = styled(SearchBox)`
  border: 0;
`;
